import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getCategory, getProductsByIds } from "../lib/api";
import { mediaUrl } from "../lib/format";
import ProductCard from "../components/ProductCard";
import Blocks from "../components/Blocks";
import PromoBar from "../components/PromoBar";
import Seo from "../components/Seo";
import { ChevronRight } from "lucide-react";

export default function CategoryPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [featuredProds, setFeaturedProds] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCat(null);
    setNotFound(false);
    getCategory(slug).then(setCat).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (cat && cat.featured_ids && cat.featured_ids.length) getProductsByIds(cat.featured_ids).then(setFeaturedProds).catch(() => {});
    else setFeaturedProds([]);
  }, [cat]);

  if (notFound) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><h1 className="font-heading text-2xl">Categoría no encontrada</h1></div>;
  if (!cat) return <div className="max-w-7xl mx-auto px-4 py-20"><div className="h-64 bg-secondary animate-pulse rounded-sm" /></div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: (cat.breadcrumb || []).map((b, i) => ({
      "@type": "ListItem", position: i + 1, name: b.name,
    })),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <Seo title={cat.seo_title || cat.name} description={cat.meta_description || `${cat.name} para panadería y pastelería profesional. Fabricación propia, directo de fábrica a su panadería. Compre ${cat.name.toLowerCase()} en FABRIPAN.`} keywords={cat.keywords || `${cat.name.toLowerCase()}, ${cat.name.toLowerCase()} panadería, comprar ${cat.name.toLowerCase()}, ${cat.name.toLowerCase()} profesional`} image={cat.image} jsonLd={jsonLd} />
      {cat.promo && cat.promo.enabled && (
        <div className="mb-4 rounded-sm overflow-hidden"><PromoBar promo={cat.promo} testid="category-promo-bar" /></div>
      )}
      <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-5" data-testid="breadcrumb">
        <Link to="/" className="hover:text-brand">Inicio</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/tienda" className="hover:text-brand">Tienda</Link>
        {(cat.breadcrumb || []).map((b) => (
          <span key={b.slug} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" /><span className="uppercase text-black">{b.name}</span></span>
        ))}
      </nav>

      <h1 className="font-heading font-bold text-4xl tracking-tighter uppercase text-black">{cat.name}</h1>
      {cat.description && <p className="text-muted-foreground mt-2 max-w-2xl">{cat.description}</p>}
      {cat.banner && (
        <div className="mt-5 aspect-[16/5] rounded-2xl overflow-hidden border border-border">
          <img src={mediaUrl(cat.banner)} alt={cat.name} className="w-full h-full object-cover" />
        </div>
      )}
      {cat.intro && <p className="text-muted-foreground mt-4 max-w-3xl leading-relaxed whitespace-pre-line">{cat.intro}</p>}

      {cat.subcategories && cat.subcategories.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          {cat.subcategories.map((s) => (
            <Link key={s.id} to={`/categoria/${s.slug}`} className="text-sm uppercase text-black border border-border rounded-sm px-3 py-1.5 hover:border-brand hover:text-brand transition-colors bg-white" data-testid={`subcat-${s.slug}`}>
              {s.name}
            </Link>
          ))}
        </div>
      )}

      {featuredProds.length > 0 && (
        <div className="mt-8" data-testid="category-featured">
          <h2 className="font-heading font-bold text-2xl tracking-tighter mb-4">Destacados</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featuredProds.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      <div className="mt-8">
        {cat.products.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center">Aún no hay productos en esta categoría.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="category-grid">
            {cat.products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>

      {cat.content_blocks && cat.content_blocks.length > 0 && (
        <div className="mt-12 max-w-3xl"><Blocks blocks={cat.content_blocks} testid="category-blocks" /></div>
      )}
    </div>
  );
}
